import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Menu, Bell, User, LogOut, Settings } from 'lucide-react';
import { logout } from '../../store/slices/authSlice';
import { PERSIAN_TEXTS } from '../../constants/persianTexts';

const HeaderContainer = styled.header`
  background: linear-gradient(90deg, #1a1a2e 0%, #16213e 100%);
  border-bottom: 2px solid #2d2d44;
  padding: 0 20px;
  height: 70px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  top: 0;
  z-index: 100;
  backdrop-filter: blur(10px);
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const MenuButton = styled.button`
  background: linear-gradient(135deg, #00d4ff 0%, #4ecdc4 100%);
  border: none;
  border-radius: 8px;
  padding: 10px;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 5px 15px rgba(0, 212, 255, 0.4);
  }
  
  @media (min-width: 769px) {
    display: none;
  }
`;

const Title = styled.h1`
  color: #ffffff;
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  background: linear-gradient(135deg, #00d4ff 0%, #4ecdc4 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const NotificationButton = styled.button`
  background: none;
  border: none;
  color: #b8b8d1;
  cursor: pointer;
  padding: 10px;
  border-radius: 8px;
  position: relative;
  transition: all 0.3s ease;
  
  &:hover {
    background: #2d2d44;
    color: #00d4ff;
  }
`;

const NotificationBadge = styled.span`
  position: absolute;
  top: 5px;
  right: 5px;
  background: #ff4757;
  color: white;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  font-size: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
`;

const UserMenu = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const UserButton = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 8px;
  padding: 8px 15px;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 5px 15px rgba(102, 126, 234, 0.4);
  }
`;

const DropdownMenu = styled(motion.div)`
  position: absolute;
  top: 100%;
  left: 0;
  background: #1a1a2e;
  border: 1px solid #2d2d44;
  border-radius: 8px;
  padding: 10px 0;
  min-width: 200px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  z-index: 1000;
`;

const DropdownItem = styled.button`
  width: 100%;
  background: none;
  border: none;
  color: #b8b8d1;
  padding: 12px 20px;
  text-align: right;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: all 0.3s ease;
  
  &:hover {
    background: #2d2d44;
    color: #00d4ff;
  }
`;

const Header = ({ onToggleSidebar }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentUser } = useSelector((state) => state.auth);
  const [showUserMenu, setShowUserMenu] = React.useState(false);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  return (
    <HeaderContainer>
      <LeftSection>
        <MenuButton onClick={onToggleSidebar}>
          <Menu size={20} />
        </MenuButton>
        <Title>پنل مدیریت تورنومنت</Title>
      </LeftSection>
      
      <RightSection>
        <NotificationButton>
          <Bell size={20} />
          <NotificationBadge>3</NotificationBadge>
        </NotificationButton>
        
        <UserMenu>
          <UserButton onClick={() => setShowUserMenu(!showUserMenu)}>
            <User size={16} />
            <span>{currentUser?.username || 'ادمین'}</span>
          </UserButton>
          
          {showUserMenu && (
            <DropdownMenu
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              <DropdownItem>
                <User size={16} />
                پروفایل
              </DropdownItem>
              <DropdownItem>
                <Settings size={16} />
                تنظیمات
              </DropdownItem>
              <DropdownItem onClick={handleLogout}>
                <LogOut size={16} />
                خروج
              </DropdownItem>
            </DropdownMenu>
          )}
        </UserMenu>
      </RightSection>
    </HeaderContainer>
  );
};

export default Header;
